import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Spinner } from 'react-bootstrap';
import { FiCheck, FiEdit2 } from 'react-icons/fi';
import usePageWidth from '../shared/usePageWidth';
import { createNotification } from '../notifications/notifications';
import { getFiles, getInfo, updateInfo } from '../../actions/portfolio';
import ProfileCard from './ProfileCard';
import MainTab, { TABLE_BLOCKS, emptyRow } from './MainTab';
import FilesTab from './FilesTab';
import ConfirmDialog from './ConfirmDialog';
import Toast, { useToast } from './Toast';
import PortfolioPrintPdf from './PrintPdf';
import styles from './styles.module.css';

/* Режим «Портфолио» — личная страница сотрудника (эталон portfolio_mockup.html).

   Экран один: заголовок с кнопками, карточка профиля из личного дела (только
   показывается), вкладки «Главная» и «Прикреплённые материалы».

   Правка охватывает всю страницу разом и правит только шесть блоков
   «Главной». Черновик живёт здесь, а не во вкладке: переключение вкладки
   правку не сбрасывает. «Отменить» возвращает черновик к снимку на момент
   входа в правку (после подтверждения), «Сохранить» отбрасывает пустые
   строки и отправляет все десять полей разом; при ошибке режим правки
   не выключается и введённое не теряется. */

// десять полей тела сохранения — все обязательны, иначе сервер отвечает 500
const LIST_FIELDS = Object.keys(TABLE_BLOCKS);
const TEXT_FIELDS = [
  'portfolio_description', 'portfolio_character',
  'portfolio_interest', 'portfolio_health', 'portfolio_limitations', 'portfolio_worth',
];

/* Право изменять портфолио. Методы режима прав не спрашивают: изменять
   может только владелец, а показывается только своё портфолио. Если же
   администратор явно выдал объекты «Постоянный состав. Портфолио» /
   «Переменный состав. Портфолио» (группа «Справочники») без права на
   изменение, кнопки «Редактировать» нет. Пока объекты не выданы вовсе,
   владелец правит своё портфолио. */
const PORTFOLIO_RIGHTS = [
  ['Справочники', 'Постоянный состав. Портфолио'],
  ['Справочники', 'Переменный состав. Портфолио'],
];

const useCanEdit = () => {
  const accessModes = useSelector((state) => state.auth.access_modes);
  const issued = PORTFOLIO_RIGHTS
    .map(([group, mode]) => accessModes && accessModes[group] && accessModes[group][mode])
    .filter((right) => right !== undefined && right !== null);
  return issued.length === 0 || issued.some((right) => right === 'Полный');
};

const cloneRows = (rows) => (Array.isArray(rows) ? rows.map((row) => ({ ...row })) : []);

// черновик — только шесть правимых блоков, глубокой копией
const draftOf = (info) => ({
  ...LIST_FIELDS.reduce((acc, field) => ({ ...acc, [field]: cloneRows(info[field]) }), {}),
  portfolio_description: info.portfolio_description == null ? '' : info.portfolio_description,
  portfolio_character: info.portfolio_character == null ? '' : info.portfolio_character,
});

const rowIsEmpty = (row) => Object.values(row).every((value) => value == null || String(value).trim() === '');

// тело сохранения: пустые строки отброшены, значения без пробелов по краям,
// служебные поля — как пришли на чтении
const buildPayload = (info, draft) => {
  const body = {};
  TEXT_FIELDS.forEach((field) => { body[field] = info[field] === undefined ? null : info[field]; });
  LIST_FIELDS.forEach((field) => {
    body[field] = (draft[field] || [])
      .map((row) => Object.keys(row).reduce((acc, key) => ({ ...acc, [key]: String(row[key] == null ? '' : row[key]).trim() }), {}))
      .filter((row) => !rowIsEmpty(row));
  });
  body.portfolio_description = String(draft.portfolio_description || '').trim();
  body.portfolio_character = String(draft.portfolio_character || '').trim();
  return body;
};

const Portfolio = ({
  user,
  selfPhoto,
  layout,
  portfolio,
  group,
  gradebook,
  semesterGrades,
  practice,
  disciplineList,
  userId,
  didMount,
  fetchGradebook,
  getSemesterGrades,
}) => {
  usePageWidth('pageWide');
  const dispatch = useDispatch();
  const canEdit = useCanEdit();

  const [tab, setTab] = useState('main');
  const [info, setInfo] = useState({ status: 'loading' });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [toastState, toast] = useToast();

  const loadInfo = useCallback(async () => {
    setInfo({ status: 'loading' });
    const result = await dispatch(getInfo());
    setInfo(result.error ? { status: 'error', error: result.error } : { status: 'ok' });
  }, [dispatch]);

  useEffect(() => {
    didMount();
    fetchGradebook(userId);
    getSemesterGrades();
    loadInfo();
    dispatch(getFiles());
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeConfirm = useCallback(() => setConfirm(null), []);

  const ask = useCallback(({ title, text, yesLabel, onYes }) => {
    setConfirm({
      title,
      text,
      yesLabel,
      onYes: () => {
        setConfirm(null);
        onYes();
      },
    });
  }, []);

  /* ---------- режим правки ---------- */
  const startEdit = () => {
    setDraft(draftOf(portfolio));
    setEditing(true);
  };

  const cancelEdit = () => {
    ask({
      title: 'Отменить правку?',
      text: 'Внесённые изменения не сохранятся.',
      yesLabel: 'Отменить правку',
      onYes: () => {
        setDraft(null);
        setEditing(false);
        toast('Правка отменена');
      },
    });
  };

  const save = async () => {
    if (!draft || saving) return;
    setSaving(true);
    const result = await dispatch(updateInfo(buildPayload(portfolio, draft)));
    setSaving(false);
    if (result.error) {
      createNotification('error', result.error, 'Портфолио не сохранено');
      return;
    }
    setDraft(null);
    setEditing(false);
    toast('Портфолио сохранено');
  };

  const changeText = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const changeCell = (field, rowIndex, key, value) => setDraft((prev) => ({
    ...prev,
    [field]: prev[field].map((row, i) => (i === rowIndex ? { ...row, [key]: value } : row)),
  }));

  const addRow = (field) => setDraft((prev) => ({
    ...prev,
    [field]: [...prev[field], emptyRow(TABLE_BLOCKS[field].columns)],
  }));

  const deleteRow = (field, rowIndex) => {
    ask({
      title: 'Удалить строку?',
      text: 'Строка будет убрана из портфолио. После сохранения вернуть её будет нельзя.',
      onYes: () => {
        setDraft((prev) => ({ ...prev, [field]: prev[field].filter((row, i) => i !== rowIndex) }));
        toast('Строка удалена');
      },
    });
  };

  /* ---------- печать ---------- */
  const pdfReady = layout
    && Object.keys(portfolio).length > 0
    && Object.keys(user).length > 0
    && group && Object.keys(group).length > 0
    && disciplineList && disciplineList.length > 0;

  const renderPrint = () => (
    <PDFDownloadLink
      document={(
        <PortfolioPrintPdf
          layoutData={layout}
          user={user}
          photo={selfPhoto}
          portfolio={portfolio}
          group={group}
          gradebook={gradebook}
          disciplineList={disciplineList}
          semesterGrades={semesterGrades}
          practice={practice}
        />
      )}
      fileName={`${user.lastname} ${user.firstname} ${user.patronymic} портфолио.pdf`}
    >
      {({ loading }) => (
        <span className={styles.btnGhost}>{loading ? 'Формирование…' : 'Печать портфолио'}</span>
      )}
    </PDFDownloadLink>
  );

  /* ---------- содержимое «Главной» ---------- */
  const renderMain = () => {
    if (info.status === 'loading') {
      return (
        <div className={styles.loader}>
          <Spinner animation="border" role="status"><span className="sr-only">Загрузка…</span></Spinner>
        </div>
      );
    }
    if (info.status === 'error') {
      return (
        <div className={styles.error}>
          <div className={styles.errorTitle}>Не удалось загрузить портфолио</div>
          {info.error}
          <div><button type="button" className={styles.btnGhost} onClick={loadInfo}>Повторить</button></div>
        </div>
      );
    }
    return (
      <MainTab
        data={editing && draft ? draft : portfolio}
        editing={editing}
        onChangeText={changeText}
        onChangeCell={changeCell}
        onAddRow={addRow}
        onDeleteRow={deleteRow}
      />
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div>
          <h1 className={styles.title}>Портфолио</h1>
          <p className={styles.sub}>Сведения о себе, достижения и прикреплённые материалы</p>
        </div>
        <div className={styles.headRight}>
          {!editing && pdfReady && renderPrint()}
          {!editing && canEdit && info.status === 'ok' && (
            <button type="button" className={styles.btn} onClick={startEdit}>
              <FiEdit2 size={17} />
              Редактировать
            </button>
          )}
          {editing && (
            <>
              <button type="button" className={styles.btnGhost} onClick={cancelEdit} disabled={saving}>
                Отменить
              </button>
              <button type="button" className={styles.btn} onClick={save} disabled={saving}>
                <FiCheck size={17} />
                {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </>
          )}
        </div>
      </div>

      <ProfileCard user={user} photo={selfPhoto} />

      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'main'}
          className={tab === 'main' ? styles.tabOn : styles.tab}
          onClick={() => setTab('main')}
        >
          Главная
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'files'}
          className={tab === 'files' ? styles.tabOn : styles.tab}
          onClick={() => setTab('files')}
        >
          Прикреплённые материалы
        </button>
      </div>

      {tab === 'main' ? renderMain() : <FilesTab confirm={ask} toast={toast} />}

      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          text={confirm.text}
          yesLabel={confirm.yesLabel}
          onYes={confirm.onYes}
          onNo={closeConfirm}
        />
      )}
      <Toast text={toastState.text} on={toastState.on} />
    </div>
  );
};

export default Portfolio;
